import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { hooks } from '../hooks';
import { text } from '../text';
import { theme } from '../constants';
import { components } from '../components';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { formatDateTime } from '../utils/formatText';
import { MdCheckCircle, MdCancel, MdChevronLeft, MdChevronRight, MdClose, MdDownload, MdPictureAsPdf, MdExpandMore } from 'react-icons/md';

const CheckCircleIcon = MdCheckCircle as any;
const CancelIcon = MdCancel as any;
const ChevronLeftIcon = MdChevronLeft as any;
const ChevronRightIcon = MdChevronRight as any;
const CloseIcon = MdClose as any;
const DownloadIcon = MdDownload as any;
const PdfIcon = MdPictureAsPdf as any;
const ExpandMoreIcon = MdExpandMore as any;

interface AttachmentItem {
  id: number;
  file_path: string;
  file_type: string;
  response_value: string | null;
  note: string | null;
  timing: string | null;
  is_primary: boolean;
  reviewer_approved: boolean;
}

interface AdditionalAttachment {
  id: number;
  file_path: string;
  file_type: string;
  note: string | null;
}

interface ChecklistItem {
  task_checklist_id: number;
  title: string;
  description: string;
  input_type: string;
  points: number;
  is_approved: boolean;
  reviewer_note: string | null;
  attachments: AttachmentItem[];
}

interface LightboxState {
  task_checklist_id: number;
  idx: number;
}

export const TaskReview: React.FC = () => {
  const { state } = useLocation();
  const navigate = hooks.useAppNavigate();
  const task = state?.task;
  const { t } = useLanguage();
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'http://localhost:5000/';

  const [checklists, setChecklists] = useState<ChecklistItem[]>([]);
  const [additionalAttachments, setAdditionalAttachments] = useState<AdditionalAttachment[]>([]);
  const [suggestedScore, setSuggestedScore] = useState<number>(100);
  const [finalScore, setFinalScore] = useState<number | string>('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [showErrorDetail, setShowErrorDetail] = useState(false);
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);
  const [additionalLightboxIdx, setAdditionalLightboxIdx] = useState<number | null>(null);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [showIncompleteConfirm, setShowIncompleteConfirm] = useState(false);
  const [incompleteReasonError, setIncompleteReasonError] = useState(false);

  useEffect(() => {
    if (!task?.id) return;
    axiosInstance.get(`/tasks/${task.id}/review-data`)
      .then(res => {
        setChecklists(res.data.checklists);
        setAdditionalAttachments(res.data.additionalAttachments || []);
        setSuggestedScore(res.data.suggestedScore);
        setFinalScore(res.data.suggestedScore);
      })
      .catch(err => {
        setError(err.response?.data?.message || (t as any).taskReview?.failedLoad || 'Failed to load review data');
        setErrorDetail(JSON.stringify(err.response?.data ?? { message: err.message }, null, 2));
      })
      .finally(() => setIsLoading(false));
  }, [task?.id]);

  const recalcScore = (updated: ChecklistItem[]) => {
    let totalProrate = 0, approvedProrate = 0;
    for (const c of updated) {
      const points = c.points || 1;
      if (c.attachments.length > 0) {
        const prorate = points / c.attachments.length;
        for (const att of c.attachments) {
          totalProrate += prorate;
          if (att.reviewer_approved) approvedProrate += prorate;
        }
      } else {
        totalProrate += points;
        if (c.is_approved) approvedProrate += points;
      }
    }
    const calc = totalProrate > 0 ? Math.round((approvedProrate / totalProrate) * 100) : 100;
    setSuggestedScore(calc);
    setFinalScore(calc);
  };

  const togglePhotoApproval = (checklistIdx: number, attId: number) => {
    const updated = checklists.map((c, i) => {
      if (i !== checklistIdx) return c;
      return {
        ...c,
        attachments: c.attachments.map(a =>
          a.id === attId ? { ...a, reviewer_approved: !a.reviewer_approved } : a
        ),
      };
    });
    setChecklists(updated);
    recalcScore(updated);
  };

  const toggleChecklistApproval = (idx: number) => {
    const updated = checklists.map((c, i) =>
      i === idx ? { ...c, is_approved: !c.is_approved } : c
    );
    setChecklists(updated);
    recalcScore(updated);
  };

  const updateNote = (idx: number, note: string) => {
    setChecklists(prev => prev.map((c, i) => i === idx ? { ...c, reviewer_note: note } : c));
  };

  const handleSubmit = async (decision: 'complete' | 'reject' | 'incomplete') => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const attachmentReviews = checklists.flatMap(c =>
        c.attachments.map(a => ({ attachment_id: a.id, is_approved: a.reviewer_approved }))
      );
      await axiosInstance.post(`/tasks/${task.id}/checklist-review`, {
        checklist_reviews: checklists.map(c => ({
          task_checklist_id: c.task_checklist_id,
          is_approved: c.is_approved,
          reviewer_note: c.reviewer_note || null,
        })),
        attachment_reviews: attachmentReviews,
        score: decision === 'incomplete' ? null : (Number(finalScore) || 0),
        decision,
        notes: notes || null,
      });
      navigate('/Task');
    } catch (err: any) {
      setError(err.response?.data?.message || (t as any).taskReview?.failedSubmit || 'Failed to submit review');
      setErrorDetail(JSON.stringify(err.response?.data ?? { message: err.message }, null, 2));
      setIsSubmitting(false);
    }
  };

  const closeErrorModal = () => {
    setError(null);
    setErrorDetail(null);
    setShowErrorDetail(false);
  };

  const isImageType = (fileType: string, filePath: string) =>
    fileType?.startsWith('image') || /\.(jpe?g|png|gif|webp|bmp)$/i.test(filePath);

  const isPdfType = (fileType: string, filePath: string) =>
    fileType === 'application/pdf' || filePath?.endsWith('.pdf');

  const additionalImages = additionalAttachments.filter(a => isImageType(a.file_type, a.file_path));

  const totalPhotos = checklists.reduce((s, c) => s + c.attachments.length, 0);
  const approvedPhotos = checklists.reduce(
    (s, c) => s + c.attachments.filter(a => a.reviewer_approved).length, 0
  );
  const scoreColor = Number(finalScore) >= 80 ? '#4CAF50' : Number(finalScore) >= 60 ? '#FF9800' : '#F44336';

  const tr = (t as any).taskReview || {};

  if (!task) {
    return (
      <div style={{ padding: 20 }}>
        <text.H3>{tr.taskNotFound || 'Task not found'}</text.H3>
        <components.Button title={t.common.goBack || 'Back'} onClick={() => navigate(-1)} containerStyle={{ marginTop: 16 }} />
      </div>
    );
  }

  // Waktu submit laporan = feedback terbaru (task.feedbacks diurutkan created_at DESC dari backend)
  const submittedAt = task.feedbacks && task.feedbacks.length > 0 ? task.feedbacks[0].created_at : null;

  const formatMinutes = (totalMins: number) => {
    const days = Math.floor(totalMins / 1440);
    const hours = Math.floor((totalMins % 1440) / 60);
    const mins = Math.floor(totalMins % 60);
    const parts: string[] = [];
    if (days > 0) parts.push(`${days} ${t.common.days || 'Days'}`);
    if (hours > 0) parts.push(`${hours} ${t.common.hours || 'Hours'}`);
    if (mins > 0 || parts.length === 0) parts.push(`${mins} ${t.common.minutes || 'Minutes'}`);
    return parts.join(' ');
  };

  const processingTimeStr = task.accumulated_work_seconds > 0
    ? formatMinutes(task.accumulated_work_seconds / 60)
    : null;
  const targetDurationStr = task.total_duration > 0 ? formatMinutes(task.total_duration) : null;

  return (
    <div style={{ backgroundColor: '#fff', minHeight: '100vh' }}>
      <components.Header title={tr.title || 'Review Checklist'} goBack={true} />

      <div style={{ padding: '64px 20px 140px', backgroundColor: theme.colors.ghostWhite }}>

        {/* Task description + timing info */}
        {(task.description || task.started_working_at || submittedAt || processingTimeStr) ? (
          <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: '12px 14px',
            marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}>
            {task.description && (
              <>
                <text.T12 style={{ color: theme.colors.mainColor, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  {tr.taskDescription || 'Task Description'}
                </text.T12>
                <text.T14 style={{ color: theme.colors.mainDark }}>{task.description}</text.T14>
              </>
            )}

            {(task.started_working_at || submittedAt || processingTimeStr) && (
              <div style={{
                marginTop: task.description ? 10 : 0,
                paddingTop: task.description ? 10 : 0,
                borderTop: task.description ? '1px solid #F3F4F6' : 'none',
                display: 'flex', flexDirection: 'column', gap: 6,
              }}>
                {task.started_working_at && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <text.T12 style={{ color: '#9CA3AF' }}>{tr.startedWorkAt || 'Started Work'}</text.T12>
                    <text.T12 style={{ color: theme.colors.mainDark, fontWeight: 600 }}>{formatDateTime(task.started_working_at)}</text.T12>
                  </div>
                )}
                {submittedAt && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <text.T12 style={{ color: '#9CA3AF' }}>{tr.submittedReportAt || 'Submitted Report'}</text.T12>
                    <text.T12 style={{ color: theme.colors.mainDark, fontWeight: 600 }}>{formatDateTime(submittedAt)}</text.T12>
                  </div>
                )}
                {processingTimeStr && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <text.T12 style={{ color: '#9CA3AF' }}>{tr.processingTime || 'Total Processing Time'}</text.T12>
                    <text.T12 style={{ color: theme.colors.mainDark, fontWeight: 600 }}>{processingTimeStr}</text.T12>
                  </div>
                )}
                {targetDurationStr && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <text.T12 style={{ color: '#9CA3AF' }}>{tr.targetDuration || 'Target Duration'}</text.T12>
                    <text.T12 style={{ color: theme.colors.mainDark, fontWeight: 600 }}>{targetDurationStr}</text.T12>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null}

        {/* Loading / Error */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <text.T14 style={{ color: theme.colors.textColor }}>{t.common.loading}</text.T14>
          </div>
        )}
        {error && (
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            }}
            onClick={closeErrorModal}
          >
            <div
              style={{
                backgroundColor: '#fff', borderRadius: 14, padding: 20,
                maxWidth: 420, width: '100%', maxHeight: '80vh', overflowY: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <text.H3 style={{ color: '#F44336', margin: 0, marginBottom: 8 }}>
                {tr.errorModalTitle || 'Something Went Wrong'}
              </text.H3>
              <text.T14 style={{ color: theme.colors.mainDark }}>{error}</text.T14>

              {errorDetail && (
                <div style={{ marginTop: 14 }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                    onClick={() => setShowErrorDetail(v => !v)}
                  >
                    <text.T12 style={{ color: theme.colors.textColor, opacity: 0.7 }}>
                      {showErrorDetail ? (tr.hideErrorDetails || 'Hide error details') : (tr.showErrorDetails || 'Show error details')}
                    </text.T12>
                    <ExpandMoreIcon
                      size={16}
                      color={theme.colors.textColor}
                      style={{ opacity: 0.7, transition: 'transform 0.2s', transform: showErrorDetail ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    />
                  </div>
                  {showErrorDetail && (
                    <pre style={{
                      backgroundColor: '#F9FAFB', borderRadius: 8, padding: 10, marginTop: 8,
                      fontSize: 11, lineHeight: 1.5, color: '#6B7280',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      maxHeight: 220, overflowY: 'auto',
                    }}>
                      {errorDetail}
                    </pre>
                  )}
                </div>
              )}

              <components.Button
                title={t.common.ok || 'OK'}
                onClick={closeErrorModal}
                containerStyle={{ marginTop: 18 }}
              />
            </div>
          </div>
        )}

        {/* Score card */}
        {!isLoading && (
          <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: 16,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 12,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <text.T12 style={{ color: theme.colors.textColor, opacity: 0.6 }}>{tr.suggestedScore || 'Suggested Score'}</text.T12>
                <text.H3 style={{ color: scoreColor, margin: 0 }}>{suggestedScore}</text.H3>
                <text.T12 style={{ color: theme.colors.textColor, opacity: 0.6 }}>
                  {approvedPhotos}/{totalPhotos} {tr.photosApproved || 'photos approved'}
                </text.T12>
              </div>
              <div style={{ textAlign: 'right' }}>
                <text.T12 style={{ color: theme.colors.textColor, opacity: 0.6, display: 'block', marginBottom: 4 }}>{tr.finalScore || 'Final Score'}</text.T12>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={finalScore}
                  onChange={e => setFinalScore(e.target.value)}
                  style={{
                    width: 72, textAlign: 'center', fontSize: 22, fontWeight: 700,
                    border: `2px solid ${scoreColor}`, borderRadius: 8, padding: '4px 8px',
                    color: scoreColor, outline: 'none',
                  }}
                />
              </div>
            </div>
            <div style={{ height: 6, backgroundColor: '#F3F4F6', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 99, transition: 'width 0.3s',
                width: `${suggestedScore}%`,
                backgroundColor: scoreColor,
              }} />
            </div>
          </div>
        )}

        {/* Checklist items */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {checklists.map((item, idx) => {
            const hasPhotos = item.input_type === 'image' && item.attachments.length > 0;
            const allApproved = hasPhotos
              ? item.attachments.every(a => a.reviewer_approved)
              : item.is_approved;
            const someApproved = hasPhotos
              ? item.attachments.some(a => a.reviewer_approved)
              : item.is_approved;

            return (
              <div key={item.task_checklist_id} style={{
                backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                border: `2px solid ${allApproved ? '#C8E6C9' : someApproved ? '#FFE0B2' : '#FFCDD2'}`,
              }}>
                {/* Checklist header */}
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: '12px 14px',
                  backgroundColor: allApproved ? '#F1F8E9' : someApproved ? '#FFF8E1' : '#FFF8F8',
                }}>
                  {!hasPhotos && (
                    <div
                      onClick={() => toggleChecklistApproval(idx)}
                      style={{ cursor: 'pointer', flexShrink: 0, marginTop: 2 }}
                    >
                      {item.is_approved
                        ? <CheckCircleIcon size={24} color="#4CAF50" />
                        : <CancelIcon size={24} color="#F44336" />
                      }
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <text.T14 style={{ fontWeight: 600, color: theme.colors.mainDark, flex: 1 }}>
                        {item.title}
                      </text.T14>
                      <div style={{
                        backgroundColor: theme.colors.mainColor + '22',
                        borderRadius: 99, padding: '2px 8px', marginLeft: 8, flexShrink: 0,
                      }}>
                        <text.T12 style={{ color: theme.colors.mainColor, fontWeight: 600 }}>
                          {item.points} pt
                        </text.T12>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Attachments */}
                {item.attachments.length > 0 && (
                  <div style={{ padding: '10px 14px', borderTop: '1px solid #F3F4F6' }}>
                    {hasPhotos ? (
                      <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                        {item.attachments.map((att, ai) => (
                          <div key={att.id} style={{ position: 'relative', flexShrink: 0 }}>
                            <img
                              src={`${imageBaseUrl}${att.file_path}`}
                              alt=""
                              onClick={() => setLightbox({ task_checklist_id: item.task_checklist_id, idx: ai })}
                              style={{
                                width: 88, height: 88, objectFit: 'cover', borderRadius: 10,
                                cursor: 'pointer', display: 'block',
                                border: `2.5px solid ${att.reviewer_approved ? '#4CAF50' : '#F44336'}`,
                              }}
                            />
                            <div
                              onClick={() => togglePhotoApproval(idx, att.id)}
                              style={{
                                position: 'absolute', bottom: 4, right: 4,
                                backgroundColor: 'rgba(255,255,255,0.92)',
                                borderRadius: '50%', cursor: 'pointer',
                                width: 24, height: 24,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                              }}
                            >
                              {att.reviewer_approved
                                ? <CheckCircleIcon size={20} color="#4CAF50" />
                                : <CancelIcon size={20} color="#F44336" />
                              }
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      item.attachments.map(att => (
                        <div key={att.id} style={{
                          backgroundColor: '#F9FAFB', borderRadius: 8, padding: '8px 10px', marginBottom: 4,
                        }}>
                          {att.response_value && (
                            <text.T14 style={{ color: theme.colors.mainDark }}>{att.response_value}</text.T14>
                          )}
                          {att.note && (
                            <text.T12 style={{ color: theme.colors.textColor, opacity: 0.7, marginTop: 2 }}>
                              {att.note}
                            </text.T12>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Reviewer note */}
                <div style={{ padding: '8px 14px 12px', borderTop: '1px solid #F3F4F6' }}>
                  <textarea
                    placeholder={tr.reviewerNotePlaceholder || 'Reviewer note (optional)...'}
                    value={item.reviewer_note || ''}
                    onChange={e => updateNote(idx, e.target.value)}
                    rows={2}
                    style={{
                      width: '100%', border: '1px solid #E5E7EB', borderRadius: 8,
                      padding: '8px 10px', fontSize: 12, resize: 'none', outline: 'none',
                      fontFamily: 'inherit', color: theme.colors.mainDark,
                      backgroundColor: '#FAFAFA', boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional attachments (view-only, no scoring) */}
        {!isLoading && additionalAttachments.length > 0 && (
          <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: 14,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginTop: 12,
          }}>
            <text.T14 style={{ fontWeight: 600, color: theme.colors.mainDark, marginBottom: 10, display: 'block' }}>
              {tr.additionalAttachments || 'Additional Attachments'}
            </text.T14>
            {/* Images row */}
            {additionalImages.length > 0 && (
              <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
                {additionalImages.map((att, ai) => (
                  <img
                    key={att.id}
                    src={`${imageBaseUrl}${att.file_path}`}
                    alt=""
                    onClick={() => setAdditionalLightboxIdx(ai)}
                    style={{
                      width: 88, height: 88, objectFit: 'cover', borderRadius: 10,
                      cursor: 'pointer', flexShrink: 0,
                      border: '2px solid #E5E7EB',
                    }}
                  />
                ))}
              </div>
            )}
            {/* Non-image files */}
            {additionalAttachments
              .filter(a => !isImageType(a.file_type, a.file_path))
              .map(att => (
                <div key={att.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  backgroundColor: '#F9FAFB', borderRadius: 8, padding: '10px 12px', marginBottom: 6,
                }}>
                  {isPdfType(att.file_type, att.file_path)
                    ? <PdfIcon size={22} color="#E53935" />
                    : <DownloadIcon size={22} color={theme.colors.mainColor} />
                  }
                  <text.T12 style={{ flex: 1, color: theme.colors.mainDark, wordBreak: 'break-all' }}>
                    {att.file_path.split('/').pop()}
                  </text.T12>
                  <a
                    href={`${imageBaseUrl}${att.file_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: 12, color: theme.colors.mainColor, fontWeight: 600,
                      textDecoration: 'none', flexShrink: 0,
                    }}
                  >
                    {isPdfType(att.file_type, att.file_path) ? (tr.view || 'View') : (tr.download || 'Download')}
                  </a>
                </div>
              ))
            }
          </div>
        )}

        {/* Overall notes */}
        {!isLoading && (
          <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: 14,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginTop: 12,
          }}>
            <text.T14 style={{ fontWeight: 600, color: theme.colors.mainDark, marginBottom: 8, display: 'block' }}>
              {tr.overallNotes || 'Overall Notes'}
            </text.T14>
            <textarea
              placeholder={tr.overallNotesPlaceholder || 'Overall review notes (optional)...'}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              style={{
                width: '100%', border: '1px solid #E5E7EB', borderRadius: 8,
                padding: '10px 12px', fontSize: 13, resize: 'none', outline: 'none',
                fontFamily: 'inherit', color: theme.colors.mainDark,
                backgroundColor: '#FAFAFA', boxSizing: 'border-box',
              }}
            />
          </div>
        )}
      </div>

      {/* Bottom action buttons */}
      {!isLoading && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          display: 'flex', justifyContent: 'center',
        }}>
          <div style={{
            width: '100%', maxWidth: 650,
            backgroundColor: '#fff',
            borderTop: '1px solid #F3F4F6',
            boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
            padding: '12px 24px 28px',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <components.Button
                title={isSubmitting ? '...' : (tr.reject || 'Reject')}
                onClick={() => setShowRejectConfirm(true)}
                style={{ backgroundColor: '#FFEBEE', color: '#F44336', border: '1px solid #FFCDD2' }}
                containerStyle={{ flex: 1 }}
              />
              <components.Button
                title={isSubmitting ? '...' : (tr.complete || 'Complete')}
                onClick={() => setShowCompleteConfirm(true)}
                style={{ backgroundColor: theme.colors.mainColor, color: '#FFFFFF' }}
                containerStyle={{ flex: 1 }}
              />
            </div>
            <components.Button
              title={isSubmitting ? '...' : (tr.incomplete || 'Incomplete')}
              onClick={() => { setIncompleteReasonError(false); setShowIncompleteConfirm(true); }}
              style={{ backgroundColor: '#fff', color: '#9E9E9E', border: '1px solid #E0E0E0' }}
              containerStyle={{ width: '100%' }}
            />
          </div>
        </div>
      )}

      {/* Reject confirmation modal */}
      {showRejectConfirm && (
        <div
          onClick={() => { if (!isSubmitting) setShowRejectConfirm(false); }}
          style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, left: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000,
            display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: '#fff', width: '100%', maxWidth: 320,
              borderRadius: 16, padding: 24, boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
            }}
          >
            <text.H4 style={{ marginBottom: 8 }}>{tr.rejectTitle || 'Reject Task?'}</text.H4>
            <text.T14 style={{ color: '#6B7280', marginBottom: 16 }}>
              {tr.rejectDesc || 'Add notes for the assignee (optional).'}
            </text.T14>
            <textarea
              placeholder={tr.overallNotesPlaceholder || 'Overall review notes (optional)...'}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #E5E7EB',
                fontSize: 14, resize: 'none', outline: 'none', boxSizing: 'border-box',
                marginBottom: 20, color: '#374151', fontFamily: 'inherit',
                backgroundColor: '#FAFAFA',
              }}
            />
            <div style={{ display: 'flex', gap: 12 }}>
              <div
                onClick={() => { if (!isSubmitting) setShowRejectConfirm(false); }}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10,
                  border: '1px solid #E5E7EB', display: 'flex',
                  justifyContent: 'center', alignItems: 'center', cursor: 'pointer',
                }}
              >
                <text.T14 style={{ color: '#374151' }}>{t.common.cancel}</text.T14>
              </div>
              <div
                onClick={() => {
                  if (isSubmitting) return;
                  setShowRejectConfirm(false);
                  handleSubmit('reject');
                }}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10,
                  backgroundColor: '#F44336',
                  display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer',
                }}
              >
                <text.T14 style={{ color: '#fff', fontWeight: '600' }}>
                  {isSubmitting ? '...' : (tr.reject || 'Reject')}
                </text.T14>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Incomplete confirmation modal */}
      {showIncompleteConfirm && (
        <div
          onClick={() => { if (!isSubmitting) setShowIncompleteConfirm(false); }}
          style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, left: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000,
            display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: '#fff', width: '100%', maxWidth: 320,
              borderRadius: 16, padding: 24, boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
            }}
          >
            <text.H4 style={{ marginBottom: 8 }}>{tr.incompleteTitle || 'Mark as Incomplete?'}</text.H4>
            <text.T14 style={{ color: '#6B7280', marginBottom: 16 }}>
              {tr.incompleteDesc || 'This task will be permanently closed. Please provide a reason.'}
            </text.T14>
            <textarea
              placeholder={tr.incompleteReasonPlaceholder || 'Reason (required)...'}
              value={notes}
              onChange={e => { setNotes(e.target.value); setIncompleteReasonError(false); }}
              rows={3}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 10,
                border: `1px solid ${incompleteReasonError ? '#F44336' : '#E5E7EB'}`,
                fontSize: 14, resize: 'none', outline: 'none', boxSizing: 'border-box',
                marginBottom: incompleteReasonError ? 6 : 20,
                color: '#374151', fontFamily: 'inherit', backgroundColor: '#FAFAFA',
              }}
            />
            {incompleteReasonError && (
              <text.T12 style={{ color: '#F44336', display: 'block', marginBottom: 16 }}>
                {tr.incompleteReasonRequired || 'Please provide a reason.'}
              </text.T12>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <div
                onClick={() => { if (!isSubmitting) setShowIncompleteConfirm(false); }}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10,
                  border: '1px solid #E5E7EB', display: 'flex',
                  justifyContent: 'center', alignItems: 'center', cursor: 'pointer',
                }}
              >
                <text.T14 style={{ color: '#374151' }}>{t.common.cancel}</text.T14>
              </div>
              <div
                onClick={() => {
                  if (isSubmitting) return;
                  if (!notes.trim()) { setIncompleteReasonError(true); return; }
                  setShowIncompleteConfirm(false);
                  handleSubmit('incomplete');
                }}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10,
                  backgroundColor: '#616161',
                  display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer',
                }}
              >
                <text.T14 style={{ color: '#fff', fontWeight: '600' }}>
                  {isSubmitting ? '...' : (tr.incomplete || 'Incomplete')}
                </text.T14>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete confirmation modal */}
      {showCompleteConfirm && (
        <div
          onClick={() => { if (!isSubmitting) setShowCompleteConfirm(false); }}
          style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, left: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000,
            display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: '#fff', width: '100%', maxWidth: 320,
              borderRadius: 16, padding: 24, boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
            }}
          >
            <text.H4 style={{ marginBottom: 8 }}>{tr.confirmTitle || 'Complete Task?'}</text.H4>
            <text.T14 style={{ color: '#6B7280', marginBottom: 20 }}>
              {tr.confirmDesc || 'This task will be marked as complete with the following score:'}
            </text.T14>
            <div style={{
              backgroundColor: theme.colors.ghostWhite, borderRadius: 12,
              padding: '16px 20px', marginBottom: 20, textAlign: 'center',
            }}>
              <text.T12 style={{ color: '#6B7280', display: 'block', marginBottom: 4 }}>{tr.finalScore || 'Final Score'}</text.T12>
              <text.H2 style={{
                color: Number(finalScore) >= 80 ? '#4CAF50' : Number(finalScore) >= 60 ? '#FF9800' : '#F44336',
                margin: 0,
              }}>
                {finalScore}
              </text.H2>
              <text.T12 style={{ color: '#6B7280', display: 'block', marginTop: 4 }}>
                {approvedPhotos}/{totalPhotos} {tr.photosApproved || 'photos approved'}
              </text.T12>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div
                onClick={() => { if (!isSubmitting) setShowCompleteConfirm(false); }}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10,
                  border: '1px solid #E5E7EB', display: 'flex',
                  justifyContent: 'center', alignItems: 'center', cursor: 'pointer',
                }}
              >
                <text.T14 style={{ color: '#374151' }}>{t.common.cancel}</text.T14>
              </div>
              <div
                onClick={() => {
                  if (isSubmitting) return;
                  setShowCompleteConfirm(false);
                  handleSubmit('complete');
                }}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10,
                  backgroundColor: theme.colors.mainColor,
                  display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer',
                }}
              >
                <text.T14 style={{ color: '#fff', fontWeight: '600' }}>
                  {isSubmitting ? '...' : (tr.complete || 'Complete')}
                </text.T14>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Checklist photo lightbox */}
      {lightbox && (() => {
        const lbChecklist = checklists.find(c => c.task_checklist_id === lightbox.task_checklist_id);
        if (!lbChecklist) return null;
        const lbAtt = lbChecklist.attachments[lightbox.idx];
        const clIdx = checklists.indexOf(lbChecklist);
        const isFirst = lightbox.idx === 0;
        const isLast = lightbox.idx === lbChecklist.attachments.length - 1;
        return (
          <div
            onClick={() => setLightbox(null)}
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0, left: 0,
              backgroundColor: 'rgba(0,0,0,0.9)',
              zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <img
              src={`${imageBaseUrl}${lbAtt?.file_path}`}
              alt=""
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: '95vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 }}
            />
            {/* Close button */}
            <div
              onClick={() => setLightbox(null)}
              style={{
                position: 'absolute', top: 20, left: 20, cursor: 'pointer',
                backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '50%',
                width: 44, height: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <CloseIcon size={26} color="#fff" />
            </div>
            {/* Toggle approval button */}
            <div
              onClick={e => {
                e.stopPropagation();
                if (clIdx >= 0 && lbAtt) togglePhotoApproval(clIdx, lbAtt.id);
              }}
              style={{
                position: 'absolute', top: 20, right: 20, cursor: 'pointer',
                backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '50%',
                width: 44, height: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {lbAtt?.reviewer_approved
                ? <CheckCircleIcon size={32} color="#4CAF50" />
                : <CancelIcon size={32} color="#F44336" />
              }
            </div>
            {/* Prev */}
            {!isFirst && (
              <div
                onClick={e => {
                  e.stopPropagation();
                  setLightbox(lb => lb ? { ...lb, idx: lb.idx - 1 } : null);
                }}
                style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#fff' }}
              >
                <ChevronLeftIcon size={36} />
              </div>
            )}
            {/* Next */}
            {!isLast && (
              <div
                onClick={e => {
                  e.stopPropagation();
                  setLightbox(lb => lb ? { ...lb, idx: lb.idx + 1 } : null);
                }}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#fff' }}
              >
                <ChevronRightIcon size={36} />
              </div>
            )}
            {lbChecklist.attachments.length > 1 && (
              <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff' }}>
                <text.T12>{lightbox.idx + 1} / {lbChecklist.attachments.length}</text.T12>
              </div>
            )}
          </div>
        );
      })()}

      {/* Additional attachments image lightbox */}
      {additionalLightboxIdx !== null && (() => {
        const isFirst = additionalLightboxIdx === 0;
        const isLast = additionalLightboxIdx === additionalImages.length - 1;
        const att = additionalImages[additionalLightboxIdx];
        return (
          <div
            onClick={() => setAdditionalLightboxIdx(null)}
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0, left: 0,
              backgroundColor: 'rgba(0,0,0,0.9)',
              zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <img
              src={`${imageBaseUrl}${att?.file_path}`}
              alt=""
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: '95vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 }}
            />
            <div
              onClick={() => setAdditionalLightboxIdx(null)}
              style={{
                position: 'absolute', top: 20, left: 20, cursor: 'pointer',
                backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '50%',
                width: 44, height: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <CloseIcon size={26} color="#fff" />
            </div>
            {!isFirst && (
              <div
                onClick={e => { e.stopPropagation(); setAdditionalLightboxIdx(i => (i ?? 1) - 1); }}
                style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#fff' }}
              >
                <ChevronLeftIcon size={36} />
              </div>
            )}
            {!isLast && (
              <div
                onClick={e => { e.stopPropagation(); setAdditionalLightboxIdx(i => (i ?? 0) + 1); }}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#fff' }}
              >
                <ChevronRightIcon size={36} />
              </div>
            )}
            {additionalImages.length > 1 && (
              <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff' }}>
                <text.T12>{additionalLightboxIdx + 1} / {additionalImages.length}</text.T12>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};
